import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as CrmActions from '../../../../state/crm/crm.actions';
import * as CrmSelectors from '../../../../state/crm/crm.selectors';

export interface CompanyDetail {
  id: string;
  name: string;
  url?: string;
  phone_number?: string;
  linkedin?: string;
  company_identifier?: string;
  industry?: string;
  number_of_employees?: number;
  revenue?: number;
  formatted_revenue?: string;
  about_company?: string;
  country?: string;
  region?: string;
  city?: string;
  address?: string;
  zip_code?: string;
  created_at: string;
  updated_at: string;
  company_contacts?: Array<{
    id: number;
    is_default: number;
    contact?: {
      id: number;
      name?: string;
      surname?: string;
      email?: string;
      phone_number?: string;
    };
  }>;
}

@Component({
  selector: 'app-view-company-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './view-company-modal.component.html',
  styleUrl: './view-company-modal.component.scss'
})
export class ViewCompanyModalComponent implements OnChanges, OnDestroy {
  @Input() isVisible: boolean = false;
  @Input() companyId: string | null = null;
  @Output() close = new EventEmitter<void>();

  company: CompanyDetail | null = null;
  loading: boolean = false;
  error: string | null = null;

  private destroy$ = new Subject<void>();

  constructor(private store: Store) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isVisible'] || changes['companyId']) {
      if (this.isVisible && this.companyId) {
        this.loadCompanyDetails();
      } else {
        this.resetModal();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadCompanyDetails(): void {
    if (!this.companyId) return;

    this.loading = true;
    this.error = null;
    this.company = null;

    // Dispatch action to load company details
    this.store.dispatch(CrmActions.loadCompanyDetail({ id: this.companyId }));

    // Subscribe to company detail selector
    this.store.select(CrmSelectors.selectCompanyDetail)
      .pipe(takeUntil(this.destroy$))
      .subscribe(company => {
        this.company = company;
        this.loading = false;
      });

    // Subscribe to loading state
    this.store.select(CrmSelectors.selectCompanyDetailLoading)
      .pipe(takeUntil(this.destroy$))
      .subscribe(loading => {
        this.loading = loading;
      });

    // Subscribe to error state
    this.store.select(CrmSelectors.selectCompanyDetailError)
      .pipe(takeUntil(this.destroy$))
      .subscribe(error => {
        this.error = error;
        if (error) {
          this.loading = false;
        }
      });
  }

  private resetModal(): void {
    this.company = null;
    this.loading = false;
    this.error = null;
  }

  onClose(): void {
    this.resetModal();
    this.close.emit();
  }

  getInitials(name: string): string {
    const words = name?.split(' ') || [];
    return words.slice(0, 2).map(word => word.charAt(0).toUpperCase()).join('');
  }

  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  formatEmployeeCount(count: number | undefined): string {
    if (!count) return '—';
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toString();
  }

  getFullAddress(company: CompanyDetail): string {
    const parts = [
      company.address,
      company.city,
      company.region,
      company.country,
      company.zip_code
    ].filter(Boolean);
    
    return parts.length > 0 ? parts.join(', ') : 'No address provided';
  }
}